import React from 'react';
import { Dictionary } from '@reduxjs/toolkit';
import { NeedHelpModalOptionsTranslated } from 'Douleutaras/constants/ccf/need-help-options';
import { Gis } from 'Douleutaras/components/Forms/gis.class';
import { TabIds } from 'views/seller-dashboard/jobs-list/types';

export interface PublicContacts {
  emails: Array<string>;
  name: string;
  phones: Array<{ number: string; type: string }>;
}

export interface CategoryModel {
  id?: number;
  cid?: number;
  slug?: string;
  title?: string;
  flow?: string;
  category_group_title?: string;
  can_use_labour_ticket?: boolean;
  image?: string;
  display_labour_ticket?: boolean;
  contacts_low?: number;
  contacts_high?: number;
  category_group?: CategoryGroup;
  geo_label?: string;
  geo_widget?: string;
  allocation?: string;
  credits_model?: boolean;
  subscription_model?: boolean;
  money_model?: boolean;
  active?: boolean;
  activate?: boolean;
  available_for_job?: boolean;
  available_for_pros?: boolean;
  available_for_new_pros?: boolean;
  search_sbs?: boolean;
  success_text_header?: string;
  success_text_footer?: string;
  target?: number;
  job_howitworks_template?: {
    contents: Array<CategoryContentTemplate>;
    id: number;
    title: string;
  };
  has_autodeals?: boolean;
  autodeals_success_text_header_singular?: string;
  autodeals_success_text_header_plural?: string;
  autodeals_success_text_footer_singular?: string;
  autodeals_success_text_footer_plural?: string;
  autoactive_success_text_footer?: string;
  remote?: boolean;
  in_form_job_title?: string;
  in_form_job_subtitle?: string;
  in_form_submit_button_text?: string;
  is_exit_popup_enabled?: boolean;
  enable_cleaning_discount_message?: boolean;
  order?: string;
  show_moneyback_guarantee?: boolean;
  jobtitle: string;
}

export interface CategoryGroup {
  slug: string;
  id: number;
  title: string;
}

export interface BusinessUnitsModel {
  jobListIllustrationUrl: string;
  jobPageIllustrationUrl: string;
  slug: string;
  title: string;
}

export interface StripeSetupIntent {
  stripeSetupIntentId: string;
  confirmed: boolean;
  processing: boolean;
  usage: SetupIntentUsageAttribute;
}

export interface CategoryContentTemplate {
  id: number;
  image: string;
  order: number;
  parent_template: number;
  text: string;
  title: string;
}

export interface ConsentsRequest {
  type: string;
  text: string;
}

export interface SpecialContentReviews {
  type: SpecialContentType.REVIEWS;
  positive: number;
  negative: number;
}

export interface SpecialContentInvoices {
  type: SpecialContentType.INVOICES;
  total: number;
  invoices: Array<{
    id: number;
    amount: number;
    due_date: string;
  }>;
}

export interface SpecialContentLostJobs {
  type: SpecialContentType.LOST_JOBS;
  total_revenue: number;
  total_count: number;
}

export interface SpecialContentProTips {
  type: SpecialContentType.PRO_TIPS;
  content: string;
}

export enum SpecialContentType {
  REVIEWS = 'reviews',
  INVOICES = 'invoices',
  PRO_TIPS = 'pro-tips',
  LOST_JOBS = 'lost-jobs'
}

export type SpecialContent = SpecialContentReviews | SpecialContentInvoices | SpecialContentLostJobs | SpecialContentProTips;

export enum SummaryType {
  DAILY = 'daily',
  WEEKLY = 'weekly'
}

export interface DailySummary {
  bu: string;
  quote_rate_period: number;
  current_mary_score: number;
  current_quote_rate: number;
  gtv_per_job_period: number;
  current_gtv_per_job: number;
  previous_mary_score: number;
  previous_quote_rate: number;
  previous_gtv_per_job: number;
  response_time_period: number;
  current_response_time: number;
  mary_score_difference: number;
  quote_rate_difference: number;
  gtv_per_job_difference: number;
  previous_response_time: number;
  fulfillment_rate_period: number;
  current_fulfillment_rate: number;
  response_time_difference: number;
  previous_fulfillment_rate: number;
  fulfillment_rate_difference: number;
  newly_assigned_customers: number;
  outdated_jobs: number;
  jobs_to_start_today: number;
  today_scheduled_meetings_with_customers: number;
  today_scheduled_quotes_to_send: number;
  today_scheduled_follow_up_calls_about_quotes: number;
  modified: string;
  special_content: SpecialContent;
}

export interface WeeklySummary {
  bu: string;
  fulfillment_rate: number;
  quotes_sent: number;
  jobs_assigned: number;
  jobs_concluded: number;
  mary_score: number;
  pro_revenue: number;
  response_time: number;
  revenue_per_completed_job: number;
  revenue_flow: number;
  user_id: number;
  week: number;
  week_title: string;
}

export interface TierChanges {
  category: Pick<CategoryModel, 'id' | 'title'>;
  current_tier: number;
  previous_tier: number;
  subcategory: number;
  title: string;
}

export interface DealsInfo {
  category: Pick<CategoryModel, 'id' | 'title'>;
  paused_until: string;
}

export type WeeklySpecialContent = TierChanges | DealsInfo;

export enum SubscriptionTierPro {
  NONE = 'none',
  BRONZE = 'bronze',
  SILVER = 'silver',
  GOLD = 'gold'
}

export interface Me extends UserV2 {
  anonymizationRequested: boolean;
  enableNotifications: boolean;
  jobHistoryEnabled: boolean;
  daily: DailySummary;
  weekly: Array<WeeklySummary>;
  totalStats: {
    tierChanges: Array<TierChanges>;
    dealsInfo: Array<DealsInfo>;
  };
  prosListExtraBidFlowEnabled: boolean;
  unreadConversations: number;
  subcategoryCt: number;
  salesforceChatEnabled: boolean;
  salesforceChatNew: boolean;
  hasProfileUpgrade: boolean;
}

export interface UserV2 {
  buyer: boolean;
  seller: boolean;
  lastLogin: string;
  isSuperuser: boolean;
  isTestUser: boolean;
  ccfEnabled: boolean;
  created: string;
  modified: string;
  username: string;
  firstName: string;
  firstNameAccusative: string;
  firstNameVocative: string;
  lastName: string;
  email: string;
  emailVerified: boolean;
  phone: string;
  isStaff: boolean;
  isStripeOnboarded: boolean;
  isCrmUser: boolean;
  isActive: boolean;
  sellerRating: string;
  sellerFeedbacks: number;
  mugshot: string;
  mugshotVideo: string;
  sellerCompanyName: string;
  sellerDisplayName: string;
  gender: 'male' | 'female';
  showCompanyName: boolean;
  lastConcludedCategoryForUser: string;
  subscriptionTier: SubscriptionTierPro;
}

export interface User {
  id?: number;
  email?: string;
  email_verified?: boolean;
  bulk_actions_allowed?: boolean;
  base_location?: { title: string };
  ccf_enabled?: boolean;
  gis?: Gis;
  status_update_enabled?: boolean;
  chat_enabled?: boolean;
  public_contacts?: PublicContacts;
  generated_id?: string;
  session?: string;
  phone?: string;
  password?: string;
  password2?: string;
  should_change_password?: boolean;
  first_name?: string;
  last_name?: string;
  last_nps_prompt?: string;
  user_type?: string;
  username?: string;
  fullname?: string;
  categories?: Array<CategoryModel>;
  gender?: string;
  get_full_name?: string;
  seller?: string;
  seller_address?: string;
  seller_travel_preferences?: Array<string>;
  seller_name_display_choice?: string;
  cities?: Array<CityResponse>;
  seller_overview?: string;
  profile_url?: string;
  creditsUser?: boolean;
  subscriptionUser?: boolean;
  feedbacks?: Array<Object>;
  contacts?: Array<Object>;
  works_on_saturdays?: boolean;
  works_on_sundays?: boolean;
  seller_years_of_service?: number;
  seller_employees_number?: number;
  seller_notification_phone?: string;
  seller_notification_email?: string;
  seller_company_name?: string;
  seller_profession?: string;
  seller_health_insurance_id?: number;
  seller_tax_office?: string;
  subscription_user?: boolean; // TODO Remove after old auth service removing
  stats?: {
    topups: number;
    jobs: number;
    credits: number;
    jobs_concluded?: number;
    buyer_revenue?: number;
  };
  credits_user?: boolean; // TODO Remove after old auth service removing
  vat?: { [s: string]: string };
  marketing_consent_granted?: boolean;
  mugshot?: string;
  mugshotVideo?: string;
  seller_rating?: number;
  seller_feedbacks?: number;
  is_dashboard_locked?: boolean;
  is_manager?: boolean;
  is_seller?: boolean;
  is_seller_money?: boolean;
  is_seller_booknow_flat?: boolean;
  is_test_user?: boolean;
  internal_username?: string;
  first_name_vocative?: string;
  date_joined?: string;
  consents?: Array<ConsentsRequest>;
  buyer?: boolean;
  is_staff?: boolean;
  is_developer?: boolean;
  external_reviews_allowed?: boolean;
  can_review_nps?: boolean;
  has_recent_cleaning?: boolean;
  municipalities?: [];
  is_saas_enabled?: boolean;
}

export enum JobCancellationReasons {
  CANCEL_MORE_OFFERS = 'cancel_more_offers',
  CANCEL_TOO_EXPENSIVE = 'cancel_too_expensive',
  CANCEL_UNINTERESTING_INTRODUCTIONS = 'cancel_uninteresting_introductions',
  CANCEL_CANCEL_TOO_LONG = 'cancel_cancel_too_long',
  CANCEL_SELLER_ME_OR_FRIEND = 'cancel_seller_me_or_friend',
  CANCEL_NOT_DOULEUTARAS = 'cancel_not_douleutaras',
  CANCEL_TERMS_REJECTED = 'cancel_terms_rejected',
  CANCEL_BETTER_TIME_OR_CHANGED_PLANS = 'cancel_better_time_or_changed_plans',
  CANCEL_COULD_NOT_REACH_PROS = 'cancel__could_not_reach_pros',
  CANCEL_WRONG_CATEGORY = 'cancel_wrong_cateogry',
  CANCEL_SIMILAR_JOB = 'cancel_similar_job',
  CANCEL_SUCCESS_PAGE = 'cancel_success_page',
  CANCEL_FAKE = 'cancel_fake',
  CANCEL_TEST_JOB = 'cancel_test_job',
  CANCEL_NO_ANSWER = 'cancel_no_answer',
  CANCEL_AREA_NOT_SERVICED = 'cancel_area_not_serviced',
  CANCEL_NO_PROS_AVAILABLE = 'cancel_no_pros_aailable',
  CANCEL_MISCOMMUNICATION_PRO_AGENT = 'cancel_miscommunication_pro_agent',
  CANCEL_MISCOMMUNICATION_PRO_CLIENT = 'cancel_miscommunication_pro_client',
  CANCEL_CLIENT_NOT_PUNCTUAL_WITH_APPT = 'cancel_client_not_punctual_with_appt',
  CANCEL_PRO_NOT_PUNCTUAL_WITH_APPT = 'cancel_pro_not_punctual_with_appt',
  CANCEL_LONG_DURATION = 'cancel_long_duration',
  CANCEL_MODIFICATION = 'cancel_modification',
  CANCEL_CLEANING_PAYMENTS_DISAGREE = 'cancel_cleaning_payments_disagree',
  CANCEL_CLEANING_CHORES_NOT_INCLUDED = 'cancel_cleaning_chores_not_included',
  CANCEL_CLEANING_NO_EQUIPMENT = 'cancel_cleaning_no_equipment',
  CANCEL_CLEANING_DESIRED_CLEANER_NOT_AVAILABLE = 'cancel_desired_cleaner_not_available',
  CANCEL_CLEANING_QUALITY_OF_SERVICE_LOW = 'cancel_cleaning_quality_of_service_low',
  CANCEL_CLEANING_CHANGE_OF_PACKAGE_SELECTED = 'cancel_cleaning_change_of_package_selected',
  CANCEL_CLEANING_NOT_SATISFIED_WITH_CLEANER = 'cancel_cleaning_not_satisfied_with_cleaner',
  CANCEL_CLEANING_LATE_CANCELLATION = 'cancel_cleaning_late_cancellation',
  CANCEL_CLEANING_DIFFICULT_ACCESS_AREA = 'cancel_cleaning_difficult_area',
  CANCEL_CLEANING_PAYMENT_FAILED = 'cancel_cleaning_payment_failed',
  CANCEL_CLEANING_NOT_SATISFIED_WITH_SERVICE = 'cancel_cleaning_not_satisfied_with_service',
  CANCEL_FULL_JOB_REFUND = 'cancel_full_job_refund',
  CANCEL_EXPIRED = 'cancel_expired',
  CANCEL_OTHER = 'cancel_other',
  CANCEL_I_WONT_PROCEED = 'cancel_i_wont_proceed'
}

export enum JobPricingModels {
  CREDITS_MODEL = 'credits_model',
  MONEY_MODEL = 'money_model'
}

export enum BusinessModel {
  CREDIT = 'credit',
  MONEY = 'money'
}

/**
 * Response interface for
 * /api/public/job/:job_id/
 */
export interface Job {
  proUsername: string;
  acceptance_pending: boolean;
  category: CategoryModel;
  cancelReason: JobCancellationReasons;
  status: JobStatus;
  pricingModel: JobPricingModels;
  title: string;
  status_seller_display: string;
  hasFixedPrice: boolean;
  id: number;
  currencyCode: keyof typeof CURRENCY;
  details: {
    values: Array<{
      attribute: AttributeModel;
    }>;
  };
  offers: number;
  book_now_entry: BookNowEntry;
  incentive: {
    id: number;
    incentive_type: string;
    main_text: string;
    secondary_text: string;
    terms_of_service: string;
  };
  valuation: boolean | null;
  basket: string;
  crm_entry_status: string;
  created: string;
  modified: string;
  location: Location;
  totalCost: string;
  askedMoreBids: boolean;
  canAskMoreBids: boolean;
  quote: string;
  buyer: {
    anonymizationRequested: boolean;
    fullName: string;
    id: number;
    phone: string;
    email: string;
  };
  isB2b: boolean;
  isUrgent: boolean;
  intention: CustomerStatusOptions;
  proofOfPaymentType: 'receipt' | 'invoice';
  providerIconColoured: string;
  providerIconGreyscale: string;
  provider: string;
  creditsAcquisitionValuation: number;
  proId: string;
  projectedRevenue: string;
  postingFlow: PostingFlows;
  subCategorySlug: string;
  // PATCH job values
  asked_more_bids: boolean;
  autodeal_id: string;
}

export interface JobPriceDefiningQuestionChoice {
  value: {
    id: number;
    title: string;
    description: string;
    help_text: string;
  };
}

export interface JobPriceDefiningQuestions {
  attribute: {
    id: number;
    title: string;
    // for datatype: currently only "choice" is used
    datatype: JobPriceDefiningQuestionsDatatype;
    choices: Array<JobPriceDefiningQuestionChoice>;
  };
  visible: boolean;
  conditionLogic: 'and' | 'or';
}

export enum JobPriceDefiningQuestionsDatatype {
  'choice' = 'choice',
  'choices' = 'choices',
  'text' = 'text',
  'float' = 'float',
  'int' = 'int',
  'date' = 'date',
  'time' = 'time',
  'bool' = 'bool',
  'geo' = 'geo',
  'file' = 'file',
  'files' = 'files'
}

export interface JobPriceDefiningAnswers {
  valueText: string;
  valueFloat: number;
  valueInt: number;
  valueDate: string;
  valueTime: string;
  valueBool: boolean;
  valueGeo: {};
  valueChoice: {
    id: number;
    title: string;
    description: string;
    help_text: string;
  };
  valueChoices: {};
}

/**
 * Response interface for
 * /api/public/job/:job_id/pros/
 */
export interface ProfileAggregates {
  behaviorAvgRating: number;
  priceAvgRating: number;
  professionalismAvgRating: number;
  punctualityAvgRating: number;
  qualityAvgRating: number;
}

export interface PaymentMethodModel {
  name: 'cash' | 'card' | 'klarna';
  icon: string;
  tooltip: string;
  order: number;
}

export interface SellerProfile extends UserV2 {
  id: number;
  username: string;
  email: string;
  sellerDisplayName: string;
  sellerOverviewPublic: string;
  mugshot: string;
  mugshotVideo: string;
  aggregates: ProfileAggregates;
  favoriteCategory: string;
  lastUnreviewedBidForUser: number;
  concludedJobsCntForUser: number;
  publicUrl: string;
  desktopBackgroundImage: string;
  mobileBackgroundImage: string;
  subscriptionTier: SubscriptionTierPro;
  availablePaymentMethods: Array<PaymentMethodModel>;
}

export interface CleanerSettings {
  acceptsPets: boolean;
  ironing: boolean;
  languages: Array<string>;
}

export interface SellerProfileCustom extends SellerProfile {
  averageRating: number;
  sellerRating: string;
  sellerFeedbacks: number;
  favoriteCategoryId: number;
  memberSince: string;
  concludedJobsCnt: number;
  cleanerSettings: CleanerSettings;
  isCreditModelSeller: boolean;
  lastConcludedCategoryForUser: string;
}

export interface SellerPortfolio {
  altImageText: string;
  description: string;
  image: string;
  title: string;
  url: string;
}

export interface ProfileBadges {
  id: string;
  group: number;
  icon: string;
  iconAlt: string;
  tag: string;
  title: string;
}

export interface ProfileCategory extends CategoryModel {
  availableForJob: boolean;
}

export interface ProfileReview {
  created: string;
  behaviorRating: number;
  comment: string;
  includesMaterials: boolean;
  materialsCost: number;
  price: number;
  priceRating: number;
  professionalismRating: number;
  punctualityRating: number;
  qualityRating: number;
  rating: number;
  weighedRating: boolean;
  attributes: [];
}
export type ProfileReviewType = JSONApiDataObject<ProfileReview>;

export interface ProfileReviewResponse {
  comment: string;
  created: string;
}

/**
 * Response interface for
 * /api/public/city/
 */
export interface CityResponse {
  active?: boolean;
  available_for_job?: boolean;
  has_only_one_municipality?: boolean;
  id?: number;
  slug?: string;
  title?: string;
}

export interface AttributeModel {
  choices: Array<ChoiceItem>;
  datatype: string;
  description: string;
  help_text: string;
  hidden: boolean;
  id: number;
  required: boolean;
  slug: string;
  title: string;
  seller_title: string;
  ui: string;
  placeholder: string;
  // datatype specific fields
  date_min_days_ahead?: number; // date control
  sub_title?: string; // plus-minus control
  sub_title_plural?: string; // plus-minus control
}

export interface ChoiceItem {
  default?: boolean;
  description?: string;
  help_text?: string;
  icon?: string;
  icon_hover?: string;
  id: number;
  order?: number;
  orig_price?: string;
  price?: string;
  title: string;
  value: number | string | null;
}

/**
 * Response interface for
 * GET /api/public/book_now_entry/:book_now_entry_id/
 */
export interface BookNowEntry {
  id: string;
  bid: number;
  job: number;
  status: BookNowStatuses;
  status_message: string;
  update_statuses: Array<string>;
  meeting_call_datetime: string;
  meeting_not_scheduled_reason: string;
  meeting_not_done_reason: string;
  cannot_be_completed_reason: string;
  quote_followup_datetime: string;
  quote_not_sent_reason: string;
  quote_not_accepted_reason: string;
  meeting_datetime: string;
  to_followup_datetime: string;
  should_be_completed_datetime: string;
  job_start: string;
  completion_estimated_datetime: string;
  completed_datetime: string;
  send_quote_on: string;
  price: number;
  title?: string;
  modified: string;
  job_will_not_be_completed_reason?: string;
  quote_amount?: string;
}

export enum JobStatus {
  UNCONFIRMED = 'unconfirmed',
  ACTIVE = 'active',
  ON_MY_WAY = 'on_my_way',
  IN_PROGRESS = 'in_progress',
  CONCLUDED = 'concluded',
  CANCELLED = 'cancelled',
  PENDING_APPROVAL = 'pending_approval',
  PENDING = 'pending'
}

/**
 * Generic API Response
 */
export interface ApiResponse<T> {
  count: number;
  pos: number;
  next: string | null;
  previous: string | null;
  results: Array<T>;
}

export interface JSONApiNormalizedEntity<T> {
  [key: number]: T;
}

interface JSONApiRelationshipData {
  id?: string;
  type?: string;
}

export interface JSONApiRelationshipObject {
  data: {
    id?: string;
    type?: string;
  } | Array<{
    id?: string;
    type?: string;
  }>;
  meta?: {
    count: number;
  };
}

interface JSONApiRelationshipResponse {
  [key: string]: JSONApiRelationshipObject;
}

export interface JSONApiDataObjectLoadable<T> extends JSONApiDataObject<T> {
  loading?: boolean;
  error?: boolean;
}

export interface JSONApiDataObject<T> {
  type: string;
  id: string;
  attributes: T;
  relationships?: JSONApiRelationshipResponse;
  links?: {
    first: string;
    last: string;
    next: string;
    prev: string;
  };
}

export interface JSONApiAttributes {
  id: number;
}

export interface FooterState {
  noLeroy: string;
  links: Array<FooterColumnLink>;
  seo: FooterSeoLinks;
  promotion: FooterPromotion;
}

export interface FooterPromotion {
  date_published: string;
  description: string;
  image: string;
  image_big: string;
  subtitle: string;
  title: string;
  url: string;
}

export enum FooterColumnLinkEnum {
  COMPANY = 'company',
  CUSTOMERS = 'customers',
  PROFESSIONALS = 'professionals'
}

export interface FooterColumnLink {
  column: FooterColumnLinkEnum;
  screen_name: string;
  url: string;
}

export interface FooterSeoLinks {
  about: string;
  bsdir: string;
  guides: string;
  locations: string;
  searches: string;
  team: string;
}

export interface FooterPromotion {
  date_published: string;
  description: string;
  image: string;
  image_big: string;
  subtitle: string;
  title: string;
  url: string;
}

export interface FooterColumnLink {
  column: FooterColumnLinkEnum;
  screen_name: string;
  url: string;
}

export interface FooterSeoLinks {
  about: string;
  bsdir: string;
  guides: string;
  locations: string;
  searches: string;
  team: string;
}

export interface ReduxEntity<T, A = undefined, B = Object> {
  initialLoad?: boolean;
  loading?: boolean;
  error: boolean;
  meta?: ReduxEntityMeta<A> & B;
  entities: Dictionary<JSONApiDataObject<T>>;
}

export interface ReduxActionObject<T> {
  payload: T;
  error?: { message: string };
  meta: {};
  type: string;
}

export interface ReduxEntityMeta<T> {
  pagination: {
    count: number;
    page: number;
    pages: number;
  };
  data: Array<{
    id: string;
  }>;
  meta: {
    [k: string]: Array<T>;
  };
}

export interface FooterState {
  noLeroy: string;
  links: Array<FooterColumnLink>;
  seo: FooterSeoLinks;
  promotion: FooterPromotion;
}

/**
 * Buyer's answer types (AttributeValue)
 */
export enum ATTRIBUTE_KEY {
  VALUE_TEXT = 'valueText',
  VALUE_FLOAT = 'valueFloat',
  VALUE_INT = 'valueInt',
  VALUE_DATE = 'valueDate',
  VALUE_TIME = 'valueTime',
  VALUE_BOOL = 'valueBool',
  VALUE_GEO = 'valueGeo',
  VALUE_CHOICE = 'valueChoice',
  VALUE_CHOICES = 'valueChoices'
}

export interface Municipality {
  title: string;
  county: {
    title: string;
    slug: string;
  };
  description: string;
  slug: string;
}

export interface Location {
  id: string;
  types: Array<string>;
  valid: boolean;
  address: {
    url: string;
    icon: string;
    name: string;
    types: Array<string>;
    geometry: {
      location: {
        lat: number;
        lng: number;
      };
      viewport: {
        east: number;
        west: number;
        north: number;
        south: number;
      };
    };
    place_id: string;
    reference: string;
    adr_address: string;
    formatted_address: string;
    html_attributions: Array<string>;
    address_components: Array<{
      types: Array<string>;
      long_name: string;
      short_name: string;
    }>;
    utc_offset_minute: number;
  };
  checked: boolean;
  latitude: number;
  longitute: number;
  description: string;
  providerName: string;
}

export enum BidStatuses {
  PURCHASE = 'purchase',
  PENDING = 'pending',
  REJECTED = 'rejected',
  ACCEPTED = 'accepted'
}

export interface JobBids {
  hiredOtherPro: boolean;
  created: string;
  modified: string;
  status: BidStatuses;
  price: number;
  fullPrice: number;
  start: string;
  currencyCode: CURRENCY;
  milestones: Array<Object>;
  tab: string;
  location: Gis;
  hiredPrice: number;
  canNotPostSuspiciousPriceReportReason: string;
  canFeedback: boolean;

  // Online payment flags
  isEligibleForOnlinePayment: boolean; // Customer's flag to indicate if the payment button is enabled or not
  isEligibleForCustomerOnlinePaymentRequest: boolean; // Pro's flag to indicate if the payment request button is enabled or not
  areCustomerOnlinePaymentsEnabled: boolean; // Common flag to indicate if the payment / request button is visible or not

  // VAT-related
  vatRate: number;
  isVatRateDeclarationVisible: boolean;

  totalAcceptedCustomerOnlinePaymentAmount: string;
  totalAcceptedCustomerOnlinePaymentNetAmount: string;
  totalPendingCustomerOnlinePaymentAmount: string;
  totalPendingCustomerOnlinePaymentNetAmount: string;
  extraBidFlow: boolean;
}

export interface JobBidsStatusBuyer {
  milestone: string;
  price: string;
  jobStartDate: string;
}

export interface MatchedProInfo {
  topProBadgeEligibility: boolean;
  availableWithin: number;
  subcategoryJobsCount: number;
  totalJobsCount: number;
  galleryItemsCount: number;
  isNewPro: boolean;
  valueForMoney: boolean;
  specialistBadge: boolean;
  sameMunicipalityBadge: boolean;
  respondsQuickly: boolean;
  oftenRebooked: boolean;
  percentileRanking: number;
}

export interface Counters {
  set_for_today: number;
  recently_assigned: number;
  to_be_updated_today: number;
  next_7_days: number;
  rest: number;
  all: number;
  concluded_by_pro: number;
  concluded_by_other_pro: number;
  concluded_outside: number;
  cancelled_by_pro: number;
  cancelled_by_client: number;
}

export interface BookNow {
  completionEstimatedDatetime: string;
  created: string;
  meetingCallDatetime: string;
  meetingDatetime: string;
  modified: string;
  notes: string;
  quoteFollowupDatetime: string;
  sendQuoteOn: string;
  status: BookNowStatuses;
  statusMessage: string;
  jobWillNotBeCompletedReason: string | null;
  start: string;
  quote: number;
}

export enum BookNowStatuses {
  JOB_POST = 'job_post',
  ACTIVE = 'active',
  CANNOT_BE_COMPLETED = 'cannot_be_completed',
  COMPLETED = 'completed',
  COMPLETION_PENDING = 'completion_pending',
  JOB_STARTED = 'job_started',
  JOB_WILL_NOT_START = 'job_will_not_start',
  JOB_WILL_NOT_BE_COMPLETED = 'job_will_not_be_completed',
  MEETING_NOT_SCHEDULED = 'meeting_not_scheduled',
  MEETING_SCHEDULED = 'meeting_scheduled',
  MEETING_DONE = 'meeting_done',
  MEETING_NOT_DONE = 'meeting_not_done',
  MEETING_CALL_SCHEDULED = 'meeting_call_scheduled',
  QUOTE_ACCEPTED = 'quote_accepted',
  QUOTE_CUSTOMER_THINKING = 'quote_customer_thinking',
  QUOTE_SENT = 'quote_sent',
  CUSTOMER_THINKING_ABOUT_IT = 'quote_customer_thinking',
  QUOTE_NOT_ACCEPTED = 'quote_not_accepted',
  QUOTE_NOT_SENT = 'quote_not_sent',
  TO_FOLLOW_UP = 'to_follow_up',
  CANCELED = 'cancelled',
  CONCLUDED = 'concluded'
}

export enum CURRENCY {
  EUR = '€',
  USD = '$'
}

export interface MilestonesData {
  status: BookNowStatuses;
  index: number;
}

export interface Conversation {
  id: number;
  created: string; // date string
  modified: string; // date string
  status: string; // enum ?
  archived_by: number; // user model id
  bid: number; // bid id
  book_now_entry: number; // book now model id
  category: number; // category model id
  job: number; // job model id
  buyer: number; // user model id
  seller: number; // user model id
  lastMessageAt: string; // date time of the last message
  isActive: boolean; // Chat input is enabled or disabled
  isEmpty: boolean; // false if has messages, true if not
}

export interface Message {
  created: string;
  modified: string;
  sentById: number;
  conversationId: number;
  deliveredTo?: {};
  seenBy?: {
    userId: number;
  };
  archived: boolean;
  content: string;
  messageType: string;
}

export interface MediaAttachment {
  cdnUrl: string;
  conversationId: number;
  created: string;
  filename: string;
  handle: string;
  metaData: MediaAttachmentMetaData;
  modified: string;
  s3Key: string;
}

export interface MediaAttachmentMetaData {
  size: number; // bytes
  mimetype: string; // image/jpeg
  width: number;
  height: number;
  blurhash: string;
}

export enum UserType {
  SELLER = 'seller',
  BUYER = 'buyer'
}

export enum CardStatus {
  POSITIVE = 'positive',
  NEGATIVE = 'negative'
}

export interface UnFormattedMilestone {
  stepNumber?: number;
  label: string | React.ReactElement;
  failed?: boolean;
  date?: string;
  price?: number;
  currencyCode?: string;
  stepInfo?: string;
  stepInfoCost?: number;
}

export interface FormattedMilestone {
  isPassed?: boolean;
  isCurrent?: boolean;
  isNext?: boolean;
  isComplete?: boolean; // Bid is "complete"
  position?: number;
  title?: string | React.ReactElement;
  failed?: boolean;
  date?: string;
  price?: string;
  stepInfo?: React.ReactElement;
  stepInfoCost?: boolean; // Should we display the cost for this step?
  currencyCode?: CURRENCY;
  stepNumber?: number;
  dateUnformatted?: string;
  moneyBackGuarantee?: boolean;
}

export interface NeedHelpChoiceTree {
  bid: NeedHelpChoiceTreeBid;
  job: NeedHelpJobChoiceTreeBid;
}

export type NeedHelpChoiceTreeBid = {
  [index in CCFModalSlugs]: NeedHelpChoiceTreeMilestone;
};

export type NeedHelpJobChoiceTreeBid = {
  [index in CCFModalSlugs]: NeedHelpModalOptionsPerJob;
};

export type NeedHelpModalOptions = keyof typeof NeedHelpModalOptionsTranslated;

export interface CCFModalTypes {
  modal: NeedHelpModalOptions;
  modal_options: CCFModalOptions;
}

export interface CCFModalOptions {
  [s: string]: {
    active: boolean;
    disabled?: boolean;
    modal?: NeedHelpModalOptions;
    modal_options?: CCFModalOptions;
    [s: string]: string | boolean | number | NeedHelpModalOptions | CCFModalOptions;
  };
}

export type NeedHelpChoiceTreeMilestone = {
  [index in NeedHelpModalOptions]: CCFModalTypes;
};

export enum NeedHelpModalOptionsPerJob {
  job_with_one_bid = 'job_with_one_bid',
  job_with_multiple_bids = 'job_with_multiple_bids'
}

export enum CCFModalSlugs {
  SELECT_CONTACT = 'select_contact_modal',
  CONTACT = 'contact_modal',
  CANCEL = 'cancel_modal',
  SUPPORT = 'support_modal',
  EXTRA_BID = 'extra_bid_modal',
  EXTRA_BID_CONTACT = 'extra_bid_modal_contact',
  WHAT_HAPPENED = 'what_happened_modal',
  NEED_HELP = 'need_help',
  WITH_NONE = 'with_none',
  SELECT_MILESTONE = 'change_milestone_modal',
  SELECT_STARTING_DATE = 'change_startingdate_modal',
  QUOTE_DETAILS = 'quote_details_modal'
}

export interface ReviewModel {
  bidId: number;
  jobId: number;
  comment: string | null;
  created: string;
  modified: string;
  rating: string;
  behaviorRating: string;
  qualityRating: string;
  priceRating: string;
  punctualityRating: string | null;
  professionalismRating: string | null;
  includesMaterials: boolean;
  materialsCost: string | null;
  price: string | null;
  isPinned: boolean;
  weighedRating: boolean;
}

export interface PinnedReview {
  buyerFullName: string;
  comment: string;
}

export interface ReviewResponseModel {
  comment: string;
  created: string;
}

/**
 * Response interface for
 * /api/public/modals/
 */
export interface ModalInfo {
  type: string;
  id: string;
  attributes: {
    title: string;
    content: string;
    slug: string;
  };
}

export enum ProsInfoModalSlugs {
  DAILY_QUOTE_RATE = 'daily-quote-rate',
  DAILY_REVENUE_PER_JOB = 'daily-revenue-per-job',
  DAILY_FULFILLMENT_RATE = 'daily-fulfillment-rate',
  DAILY_RESPONSE_TIME = 'daily-response-time',
  WEEKLY_REVENUE = 'weekly-revenue',
  WEEKLY_REVENUE_PER_JOB = 'weekly-revenue-per-job',
  WEEKLY_FULFILLMENT_RATE = 'weekly-fulfillment-rate',
  WEEKLY_BN_JOBS = 'weekly-bn-jobs',
  WEEKLY_NON_BN_JOBS = 'weekly-non-bn-jobs',
  WEEKLY_RESPONSE_TIME = 'weekly-response-time',
  WEEKLY_MARY_SCORE = 'weekly-mary-score',
  REVIEW_GUIDELINES = 'review_guidelines'
}

export interface BookNowIndex {
  status: BookNowStatuses;
  index: number;
}

export interface CustomBookNowEntry extends BookNowEntry {
  crm_status_message: string;
}

export enum CustomerStatusOptions {
  HOT = 'HOT',
  WARM = 'WARM',
  COLD = 'COLD'
}

export enum CanNotPostSuspiciousReasons {
  POSTED = 'posted',
  NOT_BIDDED = 'not_bidded'
}

export interface NoteModel {
  body: string;
  created?: string;
  id?: number;
  job: number;
  modified?: string;
  user?: number;
}

export interface ExtendedNoteModel {
  type?: string;
  id?: number;
  attributes: {
    body: string;
    created?: string;
    modified?: string;
  };
}

export interface Invoice {
  id: string;
  confirmed: boolean;
  cancelled: boolean;
  payed: boolean;
  created: string;
}

export interface UserDebt {
  has_debt: Boolean;
  abandoned_invoices_cnt: number;
  days_before_blocked: number;
  invoice_id: string;
}

export interface UserSettings {
  firstName: string;
  lastName: string;
  username: string;
  mugshot: string;
  dateJoined: string;
  sellerTitle: string;
  sellerCompanyName: string;
  vat: string;
  sellerProfession: string;
  sellerTaxOffice: string;
  sellerOverview: string;
  sellerAddress: string;
  sellerTravelPreferences: [];
  worksOnSaturdays: boolean;
  worksOnSundays: boolean;
  phone: string;
  email: string;
  token: string;
  enableNotifications: boolean;
}

export interface SplideRef {
  splide: {
    go(index: number): void;
    on(event: string, eventCallback: (newIndex, prevIndex, destIndex) => void): void;
    sync(SplideRef): void;
  };
}

export interface FeedbackResponse {
  id: string;
  type: string;
  attributes: {
    comment: string;
    created: string;
  };
}

// Error object coming from the API on form errors
export interface ApiErrorObject {
  [key: string]: Array<string> | string;
}

export interface OuterJobBnEntry {
  id: string;
  bid: number;
  job: number;
  status: BookNowStatuses;
  statusMessage: string;
  updateStatuses: Array<string>;
  meetingCallDatetime: string;
  meetingNotScheduledReason: string;
  meetingNotDoneReason: string;
  cannotBeCompletedReason: string;
  quoteFollowupDatetime: string;
  quoteNotSentReason: string;
  quoteNotAcceptedReason: string;
  meetingDatetime: string;
  toFollowupDatetime: string;
  shouldBeCompletedDatetime: string;
  jobStart: string;
  completionEstimatedDatetime: string;
  completedDatetime: string;
  sendQuoteOn: string;
  price: number;
  title?: string;
  modified: string;
  jobWillNotBeCompletedReason?: string;
  jobWillNotBeCompletedOtherReason: string;
  quoteAmount?: string;
}

export interface OuterJob {
  title: string;
  details: string;
  customer: {
    fullName: string;
    email: string;
    phone: string;
    landlinePhone: string;
  };
  created: string;
  modified: string;
  status: string;
  address: string;
  bnEntry: OuterJobBnEntry;
}

export enum BillingChannels {
  CREDITS = 'credits'
}

export interface Billing {
  id: number;
  title: string;
  ios_iap_product_id: number;
  discount_raw: number;
  color: string;
  featured: boolean;
  price_raw: string;
  vat: boolean;
  sku: string;
  active: boolean;
  show: boolean;
  vat_raw: string;
  currency: string;
  channel: BillingChannels;
  credits: number;
  bids: number;
  categories: {
    name_to_show: string;
  };
  duration: number;
  autotopup: boolean;
  autotopup_price_raw: string;
  autotopup_nopenalty: number;
  price: number;
  price_net: string;
  vat_amount: string;
  offline_counterpart: number;
  backend: string;
}

export enum SetupIntentUsageAttribute {
  AUTOTOPUP = 'autotopup',
  AUTOCHARGE = 'autocharge'
}

// Stripe auto top-up and autocharge
export interface SetupIntent {
  paymentMethodId: string; // In format of "pm_1PcsmVGWv1y48tEFwt7xJQRb"
  product_sku?: string; // example "su_50_creds_jun19_24_cc_a"
  usage?: SetupIntentUsageAttribute;
}

export interface BidsWithOuter {
  instanceType: string;
  tab: TabIds;
}

export enum BidTypePrefix {
  INTERNAL = 'internal',
  EXTERNAL = 'outer'
}

export interface CategoryMenu {
  title: string;
  icon: string;
  url: string;
  children: Array<{
    title: string;
    url: string;
  }>;
}

export interface ProsAvailableInfo {
  id: number;
  category: number;
  municipality: string;
  pros_available: number;
  conditions: Array<{
    slug: string;
    choice_value: number;
    value_date: string;
  }>;
  metropolitan_area: string;
}

export interface QuoteRange {
  id: number;
  title: string;
  category: number;
  price_min: number;
  price_max: number;
  conditions: Array<string>;
  not_enough_data: boolean;
}

export interface ProAttributes {
  username: string;
  sellerDisplayName: string;
  sellerRating: number;
  sellerFeedbacks: number;
  proMugshot: string;
  availablePaymentMethods?: [];
  autoDealId?: number;
  subscriptionTier?: string; // e.g. "gold"
  multiBid?: boolean;
  subcategoryJobsCount?: number;
  totalJobsCount?: number;
  galleryItemsCount?: number;
  sameMunicipalityBadge?: boolean;
  isNewPro?: boolean;
  valueForMoney?: boolean;
  oftenRebooked?: boolean;
  respondsQuickly?: boolean;
  availableWithin?: string;
  specialistBadge?: boolean;
  hasNoCapacity?: boolean;
  topProBadgeEligibility?: boolean;
  pinnedReview?: PinnedReview;
}

export interface ProListItem {
  type: 'pros-list-pro';
  id: string;
  attributes: ProAttributes;
}

export enum PostingFlows {
  DIRECT = 'direct',
  LEGACY = 'legacy',
  PROS_LIST = 'pros_list'
}

export interface PostingFlow {
  category_slug: string;
  sub_category_slug: string;
  business_unit_slug?: string;
  bids_objective?: string;
  county_slug: string;
  full_rollout: boolean;
  municipality_slug: string;
  posting_flow: PostingFlows;
  pros_available_info: ProsAvailableInfo;
  quote_range: QuoteRange;
  location: Location;
  max_bids: number;
// tslint:disable-next-line:max-file-line-count
}
